import React, {
  ChangeEvent,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import Box from "../../UI/Box/Box";
import { MixtureListStyled } from "./MixtureListStyled";
import MixtureListItem from "./MixtureListItem/MixtureListItem";
import { useSelector } from "react-redux";
import { getDataFromMixture } from "../Mixture/selectors";
import { TranslationContextInterface } from "../../entities/Language";
import { TranslationContext } from "../../context/TranslationContext";
import { getValueForKey } from "../../context/translationsUtils";
import EditIcon from "../../UI/Icons/EditIcon";
import { useAppDispatch } from "../../store/store";
import { setMixtureName } from "../Mixture/MixtureSlice";
import { TEST_ID } from "../../shared/testid";
import { MixtureChooseEnum, forbiddenCharacters } from "../../entities/Mixture";
import TooltipComponent from "../TooltipComponent";
import MixtureListChoose from "./MixtureListChoose";
import MixtureListProgress from "./MixtureListProgress";
import { getCalculatedVolume } from "../Calculation/slice/selectors";
import { MODAL_TYPES } from "../../entities/Modal";
import { onOpenModal } from "../Modal/slice/ModalSlice";
import { getModalData } from "../Modal/slice/selectors";

const MixtureList: React.FC = () => {
  const [hasForbiddenCharacter, setHasForbiddenCharacter] =
    useState<boolean>(false);
  const dispatch = useAppDispatch();
  const { isOpen: modalIsOpen } = useSelector(getModalData);
  const { translations }: TranslationContextInterface =
    useContext(TranslationContext);

  const appearedOnce = useRef<boolean>(false);

  const {
    condiments = [],
    totalParts,
    totalGrams,
    mixtureOption,
    nameHasError,
    name,
  } = useSelector(getDataFromMixture);
  const { minVolumeOfMix: minWeight, maxVolumeOfMix: maxWeight } =
    useSelector(getCalculatedVolume);
  const ref = useRef<null | HTMLInputElement>(null);

  useEffect(() => {
    if (!totalGrams || !minWeight || !maxWeight) {
      return;
    }

    if (totalGrams < minWeight || totalGrams > maxWeight) {
      if (!appearedOnce.current) {
        appearedOnce.current = true;
        dispatch(
          onOpenModal({
            modalType: MODAL_TYPES.ALERT,
            content: {
              name: getValueForKey(translations, "alerts.alert_title"),
              description: getValueForKey(
                translations,
                "edit_mix.step_1_warning",
              ),
            },
          }),
        );
      }
    } else {
      appearedOnce.current = false;
    }
  }, [totalGrams, minWeight, maxWeight]);

  useEffect(() => {
    if (nameHasError && ref?.current && !modalIsOpen) {
      setTimeout(() => {
        window.scrollTo(0, 0);
        ref?.current?.focus({ preventScroll: true });
      }, 100);
    }
  }, [nameHasError, ref.current, modalIsOpen]);

  const onChangeMixName = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value.trimStart();
    const regexPattern = new RegExp(`[${forbiddenCharacters.join("")}]`);

    if (value.length < 30 && !regexPattern.test(value)) {
      dispatch(setMixtureName(value));
    }

    if (regexPattern.test(value)) {
      setHasForbiddenCharacter(true);
      setTimeout(() => {
        setHasForbiddenCharacter(false);
      }, 1000);
    }
  };

  return (
    <MixtureListStyled>
      <Box className="no-space">
        <div className="mixture-name">
          <div className="mixture-name__icon">
            <EditIcon className={`${nameHasError ? "has-error" : ""}`} />
          </div>
          <div className="mixture-name__input">
            <input
              type="text"
              value={name}
              onChange={onChangeMixName}
              data-testid={TEST_ID.CREATE_MIX.NAME}
              id="mixture-name__controll"
              placeholder={getValueForKey(
                translations,
                "edit_mix.name",
                undefined,
                true,
              )}
              ref={ref}
              onBlur={(e) => {
                if (e.relatedTarget === null) {
                  e.target.focus();
                }
              }}
              className={`${nameHasError ? "has-error" : ""}`}
            />
            {nameHasError && (
              <div className="mixture-error">
                {getValueForKey(translations, "edit_mix.provideName")}
              </div>
            )}
            <TooltipComponent
              isOpen={hasForbiddenCharacter}
              anchor="#mixture-name__controll"
              place="bottom"
            >
              <div className="input-jars">
                <div className="input-jars__description">
                  {getValueForKey(
                    translations,
                    "edit_mix.forbidden_characters",
                    {
                      forbidden_characters: forbiddenCharacters.join(" "),
                    },
                  )}
                </div>
              </div>
            </TooltipComponent>
          </div>
        </div>
        <MixtureListChoose />
        <MixtureListProgress />
        <div className="mixture-instruction">
          {getValueForKey(translations, "edit_mix.step_1_hint")}
        </div>

        <div className="mixture-labels">
          <div className="mixture-label">
            <span>
              {getValueForKey(translations, "edit_mix.ingredients_hint")}
            </span>
            <span>{condiments.length}</span>
          </div>
          {mixtureOption === MixtureChooseEnum.PARTS && (
            <div className="mixture-label">
              <span>{getValueForKey(translations, "edit_mix.parts_hint")}</span>
              <span>{totalParts}</span>
            </div>
          )}
        </div>
        <div
          className="mixture-items"
          data-testid={TEST_ID.MIX_CONFIGURATION.ITEMS}
        >
          {condiments.map((item) => (
            <MixtureListItem key={item.id} condiment={item} />
          ))}
        </div>
      </Box>
    </MixtureListStyled>
  );
};

export default MixtureList;
